import React, { useState } from 'react';

const EntityManagement = ({ entities, onCreateEntity, onUpdateEntity, onDeleteEntity, setIsEditing }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [formData, setFormData] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (ativo) => {
    return (
      <span className={`badge ${ativo ? 'approved' : 'suspended'}`}>
        {ativo ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  const handleCreateEntity = () => {
    setFormData({
      id: '',  // Código de 3 caracteres
      nome: '',
      descricao: '',
      ativo: true
    });
    setShowCreateModal(true);
    setIsEditing(true);
  };

  const handleEditEntity = (entity) => {
    setSelectedEntity(entity);
    setFormData({
      nome: entity.nome,
      descricao: entity.descricao || '',
      ativo: entity.ativo
    });
    setShowEditModal(true);
    setIsEditing(true);
  };

  const handleDeleteEntity = async (entity) => {
    // Check if entity has any associated data
    const hasAssociatedData = (entity.total_events > 0) || (entity.total_rondas > 0) || (entity.total_volunteers > 0);
    const isDeactivation = hasAssociatedData;

    let warningMessage = `Tem a certeza que deseja ${isDeactivation ? 'desativar' : 'eliminar'} a entidade "${entity.nome}"?`;

    if (isDeactivation) {
      const associations = [];
      if (entity.total_events > 0) associations.push(`${entity.total_events} eventos`);
      if (entity.total_rondas > 0) associations.push(`${entity.total_rondas} rondas`);
      if (entity.total_volunteers > 0) associations.push(`${entity.total_volunteers} voluntários`);

      warningMessage += `\n\nEsta entidade tem ${associations.join(', ')} associados e será apenas desativada.`;
    }

    const confirmed = window.confirm(warningMessage);

    if (confirmed) {
      try {
        if (isDeactivation) {
          // If entity has associated data, just deactivate it (soft delete)
          await onUpdateEntity(entity.id, { ...entity, ativo: false });
        } else {
          // If entity has no associated data, actually delete it
          await onDeleteEntity(entity.id);
        }
      } catch (error) {
        alert(`Erro ao ${isDeactivation ? 'desativar' : 'eliminar'} entidade: ` + (error.response?.data?.message || error.message));
      }
    }
  };

  const submitCreateEntity = async () => {
    if (!formData.id || !formData.id.trim()) {
      alert('Código é obrigatório');
      return;
    }

    if (formData.id.length !== 3) {
      alert('Código deve ter exatamente 3 caracteres');
      return;
    }

    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    try {
      await onCreateEntity(formData);
      setShowCreateModal(false);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao criar entidade: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitEditEntity = async () => {
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    try {
      await onUpdateEntity(selectedEntity.id, formData);
      setShowEditModal(false);
      setSelectedEntity(null);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao atualizar entidade: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedEntity(null);
    setFormData({});
    setIsEditing(false);
  };

  return (
    <>
      <div className="card-header">
        <h3>Gestão de Entidades</h3>
        <button className="btn btn-success" onClick={handleCreateEntity}>
          + Nova Entidade
        </button>
      </div>

      {entities.length === 0 ? (
        <div className="empty-state">
          <h3>Nenhuma entidade encontrada</h3>
          <p>Crie a primeira entidade para começar a registar eventos.</p>
          <button className="btn btn-primary" onClick={handleCreateEntity}>
            Criar Entidade
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Status</th>
                <th>Total Eventos</th>
                <th>Criado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {entities.map(entity => (
                <tr key={entity.id}>
                  <td><strong>{entity.id}</strong></td>
                  <td>
                    <strong>{entity.nome}</strong>
                  </td>
                  <td>
                    <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entity.descricao || <em style={{ color: '#6c757d' }}>Sem descrição</em>}
                    </div>
                  </td>
                  <td>{getStatusBadge(entity.ativo)}</td>
                  <td>
                    <span className={`badge ${entity.total_events > 0 ? 'primary' : 'secondary'}`}>
                      {entity.total_events}
                    </span>
                  </td>
                  <td>{formatDate(entity.created_at)}</td>
                  <td>
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleEditEntity(entity)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteEntity(entity)}
                    >
                      {(entity.total_events > 0 || entity.total_rondas > 0 || entity.total_volunteers > 0) ? 'Desativar' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Entity Modal */}
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Criar Nova Entidade</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>

            <div className="form-group">
              <label htmlFor="id">Código da Entidade * (3 caracteres)</label>
              <input
                type="text"
                id="id"
                className="form-control"
                value={formData.id || ''}
                onChange={(e) => setFormData({...formData, id: e.target.value.toUpperCase().slice(0, 3)})}
                placeholder="Ex: CDP, AMI, LAR"
                maxLength={3}
                required
                style={{ textTransform: 'uppercase' }}
              />
              <small className="form-text">
                Código único de 3 letras para identificar a entidade
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="nome">Nome da Entidade *</label>
              <input
                type="text"
                id="nome"
                className="form-control"
                value={formData.nome || ''}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Casa do Povo de Lisboa"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="descricao">Descrição</label>
              <textarea 
                id="descricao"
                className="form-control"
                rows="3"
                value={formData.descricao || ''}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição detalhada da entidade (opcional)"
              />
            </div>
            
            <div className="form-group">
              <label>
                <input 
                  type="checkbox"
                  checked={formData.ativo || false}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                />
                {' '}Entidade ativa
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={submitCreateEntity}
                disabled={!formData.id?.trim() || formData.id.length !== 3 || !formData.nome?.trim()}
              >
                Criar Entidade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entity Modal */}
      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Editar Entidade</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>
            
            <div className="form-group">
              <label>Código: <strong>{selectedEntity?.id}</strong></label>
            </div>
            
            <div className="form-group">
              <label htmlFor="edit_nome">Nome da Entidade *</label>
              <input 
                type="text"
                id="edit_nome"
                className="form-control"
                value={formData.nome || ''}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="edit_descricao">Descrição</label>
              <textarea 
                id="edit_descricao"
                className="form-control"
                rows="3"
                value={formData.descricao || ''}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição detalhada da entidade (opcional)"
              />
            </div>
            
            <div className="form-group">
              <label>
                <input 
                  type="checkbox"
                  checked={formData.ativo || false}
                  onChange={(e) => setFormData({...formData, ativo: e.target.checked})}
                />
                {' '}Entidade ativa
              </label>
              {selectedEntity?.total_events > 0 && !formData.ativo && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  ⚠️ Desativar esta entidade irá impedir novos eventos, mas preservará os {selectedEntity.total_events} eventos existentes
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={submitEditEntity}
                disabled={!formData.nome?.trim()}
              >
                Guardar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EntityManagement;